import { useState, useCallback, useMemo } from 'react';
import { useBulkCreateLeads } from '@/hooks/useLeadsOperations';
import { useLeads } from '@/hooks/useLeads';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { validateWorkspaceForCreate } from '@/lib/workspaceValidation';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Upload, 
  FileSpreadsheet, 
  Loader2, 
  CheckCircle2, 
  AlertTriangle, 
  XCircle,
  FileText,
  ArrowRight,
  ArrowLeft,
  Users,
  Info,
} from 'lucide-react';

interface CSVImportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CSVData {
  headers: string[];
  rows: string[][];
  fileName: string;
  fileSize: number;
}

interface ColumnMapping {
  name: string;
  email: string;
  phone: string;
  company: string;
  source: string;
  notes: string;
  temperature: string;
  tags: string;
}

interface ImportSummary {
  total: number;
  valid: number;
  duplicates: number;
  invalid: number;
  errors: { row: number; reason: string }[];
}

type Step = 'select' | 'preview' | 'confirm' | 'importing' | 'done' | 'error';

// Header synonyms for auto-mapping
const HEADER_SYNONYMS: Record<keyof ColumnMapping, string[]> = {
  name: ['name', 'full_name', 'fullname', 'customer', 'lead_name', 'contact', 'contact_name', 'customer_name', 'nome', 'cliente'],
  email: ['email', 'e-mail', 'mail', 'e_mail', 'emailaddress', 'email_address', 'correio', 'email_comercial'],
  phone: ['phone', 'phone_number', 'mobile', 'tel', 'telephone', 'whatsapp', 'celular', 'telefone', 'cell', 'mobile_number', 'fone'],
  company: ['company', 'organization', 'org', 'empresa', 'company_name', 'organization_name', 'business', 'firma'],
  source: ['source', 'channel', 'origin', 'origem', 'fonte', 'lead_source', 'referral'],
  notes: ['notes', 'note', 'message', 'description', 'obs', 'observacao', 'observações', 'comentario', 'comments', 'comment'],
  temperature: ['temperature', 'temp', 'status', 'lead_status', 'priority', 'urgency', 'temperatura'],
  tags: ['tags', 'tag', 'categories', 'category', 'labels', 'label', 'etiquetas', 'categorias'],
};

const LEAD_FIELDS = [
  { key: 'name', label: 'Name', required: false, description: 'Contact name (auto-generated from email/phone if missing)' },
  { key: 'email', label: 'Email', required: false, description: 'Email address' },
  { key: 'phone', label: 'Phone', required: false, description: 'Phone / WhatsApp' },
  { key: 'company', label: 'Company', required: false, description: 'Company name' },
  { key: 'source', label: 'Source', required: false, description: 'Lead source' },
  { key: 'notes', label: 'Notes', required: false, description: 'Additional notes' },
] as const;

function normalizeHeader(header: string): string {
  return (header || '')
    .toLowerCase()
    .trim()
    .replace(/[\s\-]+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

function autoMapHeaders(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = {
    name: '',
    email: '',
    phone: '',
    company: '',
    source: '',
    notes: '',
    temperature: '',
  };

  headers.forEach((header, index) => {
    const normalized = normalizeHeader(header);
    
    for (const [field, synonyms] of Object.entries(HEADER_SYNONYMS)) {
      if (synonyms.some(syn => normalized.includes(syn) || syn.includes(normalized))) {
        if (!mapping[field as keyof ColumnMapping]) {
          mapping[field as keyof ColumnMapping] = String(index);
        }
      }
    }
  });

  return mapping;
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function normalizePhone(phone: string): string {
  // Keep + prefix, remove all other non-digit characters
  const cleaned = phone.replace(/[^\d+]/g, '');
  return cleaned.startsWith('+') ? cleaned : cleaned;
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function CSVImportModal({ open, onOpenChange }: CSVImportModalProps) {
  const [step, setStep] = useState<Step>('select');
  const [csvData, setCsvData] = useState<CSVData | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({
    name: '',
    email: '',
    phone: '',
    company: '',
    source: '',
    notes: '',
    temperature: '',
    tags: '',
  });
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [normalizePhones, setNormalizePhones] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { activeWorkspaceId, mode } = useWorkspaceScope();
  const bulkCreate = useBulkCreateLeads();
  const { data: existingLeads } = useLeads();
  
  // CRITICAL: Hard-stop if no workspace or in "All" mode
  const validation = validateWorkspaceForCreate(activeWorkspaceId, mode, 'import leads');
  if (!validation.isValid) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Workspace Required
            </DialogTitle>
            <DialogDescription>
              {mode === 'all' 
                ? 'Cannot import leads in "All" mode. Please select a specific workspace.'
                : 'You need an active workspace to import leads.'}
            </DialogDescription>
          </DialogHeader>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{mode === 'all' ? 'Workspace Selection Required' : 'No Workspace'}</AlertTitle>
            <AlertDescription>
              {mode === 'all'
                ? 'Please select a specific workspace from the workspace switcher to import leads.'
                : 'Please wait for your workspace to be initialized, or contact support if this issue persists.'}
            </AlertDescription>
          </Alert>
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)} variant="outline">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Calculate import summary when mapping changes
  const calculatedSummary = useMemo((): ImportSummary | null => {
    // Require at least name OR email OR phone to be mapped
    if (!csvData || (!mapping.name && !mapping.email && !mapping.phone)) return null;

    const summary: ImportSummary = {
      total: csvData.rows.length,
      valid: 0,
      duplicates: 0,
      invalid: 0,
      errors: [],
    };

    const existingEmails = new Set(
      existingLeads?.map(l => l.email?.toLowerCase()).filter(Boolean) || []
    );
    const existingPhones = new Set(
      existingLeads?.map(l => l.phone?.replace(/\D/g, '')).filter(Boolean) || []
    );
    const seenEmails = new Set<string>();
    const seenPhones = new Set<string>();

    csvData.rows.forEach((row, index) => {
      let name = mapping.name ? (row[parseInt(mapping.name)] || '').trim() : '';
      const email = mapping.email ? (row[parseInt(mapping.email)] || '').trim().toLowerCase() : '';
      const phone = mapping.phone ? (row[parseInt(mapping.phone)] || '').trim() : '';
      const cleanPhone = phone.replace(/\D/g, '');

      // Intelligent name generation: if name is missing, use email or phone
      if (!name) {
        if (email) {
          // Extract name from email (part before @)
          name = email.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        } else if (phone) {
          name = `Contact ${phone}`;
        } else {
          summary.invalid++;
          summary.errors.push({ row: index + 2, reason: 'Missing name, email, and phone' });
          return;
        }
      }

      // Validate email format if provided
      if (email && !isValidEmail(email)) {
        summary.invalid++;
        summary.errors.push({ row: index + 2, reason: `Invalid email: ${email}` });
        return;
      }

      // Check for duplicates
      if (skipDuplicates) {
        const isDuplicate = 
          (email && (existingEmails.has(email) || seenEmails.has(email))) ||
          (cleanPhone && (existingPhones.has(cleanPhone) || seenPhones.has(cleanPhone)));
        
        if (isDuplicate) {
          summary.duplicates++;
          return;
        }
      }

      // Track for internal duplicate detection
      if (email) seenEmails.add(email);
      if (cleanPhone) seenPhones.add(cleanPhone);

      summary.valid++;
    });

    return summary;
  }, [csvData, mapping, skipDuplicates, existingLeads]);

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;

      console.log('[CSVImport] File selected:', file.name, 'Size:', file.size);
      setErrorMessage('');
      setIsLoading(true);

      // Validate file type
      const isCSV = file.name.toLowerCase().endsWith('.csv') || 
                    file.type === 'text/csv' || 
                    file.type === 'application/vnd.ms-excel';
      
      if (!isCSV) {
        console.error('[CSVImport] Invalid file type:', file.type);
        setErrorMessage('Please upload a .csv file.');
        setStep('error');
        setIsLoading(false);
        toast({
          title: 'Invalid File',
          description: 'Please upload a CSV file.',
          variant: 'destructive',
        });
        return;
      }

      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        console.error('[CSVImport] File too large:', file.size);
        setErrorMessage('File is too large. Maximum size is 10MB.');
        setStep('error');
        setIsLoading(false);
        return;
      }

      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          const text = event.target?.result as string;
          console.log('[CSVImport] File read successfully, length:', text?.length);
          
          if (!text || !text.trim()) {
            setErrorMessage('The file is empty. Please upload a CSV with data.');
            setStep('error');
            setIsLoading(false);
            return;
          }

          const lines = text.trim().split(/\r?\n/).filter(line => line.trim().length > 0);
          
          if (lines.length < 2) {
            setErrorMessage('CSV needs at least a header row and one data row.');
            setStep('error');
            setIsLoading(false);
            return;
          }

          const headers = parseCSVLine(lines[0]);
          const rows: string[][] = [];
          
          for (let i = 1; i < lines.length; i++) {
            try {
              const row = parseCSVLine(lines[i]);
              if (row.some(cell => cell.trim().length > 0)) {
                rows.push(row);
              }
            } catch (err) {
              console.warn(`[CSVImport] Skipping malformed row ${i}:`, err);
            }
          }

          if (rows.length === 0) {
            setErrorMessage('No valid data rows found in the CSV.');
            setStep('error');
            setIsLoading(false);
            return;
          }

          console.log('[CSVImport] Parsed successfully:', headers.length, 'columns,', rows.length, 'rows');

          // Auto-map headers
          const autoMapping = autoMapHeaders(headers);
          console.log('[CSVImport] Auto-mapping:', autoMapping);

          setCsvData({
            headers,
            rows,
            fileName: file.name,
            fileSize: file.size,
          });
          setMapping(autoMapping);
          setStep('preview');
          setIsLoading(false);

        } catch (err) {
          console.error('[CSVImport] Parse error:', err);
          setErrorMessage('Failed to parse CSV. Please check the file format.');
          setStep('error');
          setIsLoading(false);
        }
      };

      reader.onerror = (error) => {
        console.error('[CSVImport] FileReader error:', error);
        setErrorMessage('Failed to read the file. Please try again.');
        setStep('error');
        setIsLoading(false);
      };

      reader.readAsText(file);

      // Reset input so same file can be selected again
      e.target.value = '';

    } catch (error) {
      console.error('[CSVImport] Unexpected error:', error);
      setErrorMessage('An unexpected error occurred.');
      setStep('error');
      setIsLoading(false);
    }
  }, []);

  const handleImport = async () => {
    // Validate workspace before import
    const validation = validateWorkspaceForCreate(activeWorkspaceId, mode, 'import leads');
    if (!validation.isValid) {
      return; // Toast already shown by validateWorkspaceForCreate
    }

    // Require at least name OR email OR phone to be mapped
    if (!csvData || (!mapping.name && !mapping.email && !mapping.phone) || !calculatedSummary) {
      console.error('[CSVImport] Cannot import: missing data or mapping');
      toast({
        title: 'Mapping required',
        description: 'Please map at least Name, Email, or Phone column.',
        variant: 'destructive',
      });
      return;
    }

    if (calculatedSummary.valid === 0) {
      toast({
        title: 'No valid leads',
        description: 'Please fix the validation errors before importing.',
        variant: 'destructive',
      });
      return;
    }

    // CRITICAL: Ensure authentication is present before import
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      toast({
        title: 'Not authenticated',
        description: 'Please log in to import leads.',
        variant: 'destructive',
      });
      return;
    }

    console.log('[CSVImport] Starting import of', calculatedSummary.valid, 'leads...');
    setStep('importing');

    try {
      const existingEmails = new Set(
        existingLeads?.map(l => l.email?.toLowerCase()).filter(Boolean) || []
      );
      const existingPhones = new Set(
        existingLeads?.map(l => l.phone?.replace(/\D/g, '')).filter(Boolean) || []
      );
      const seenEmails = new Set<string>();
      const seenPhones = new Set<string>();

      const leads = csvData.rows
        .map(row => {
          try {
            let name = mapping.name ? (row[parseInt(mapping.name)] || '').trim() : '';
            const email = mapping.email ? (row[parseInt(mapping.email)] || '').trim().toLowerCase() : null;
            let phone = mapping.phone ? (row[parseInt(mapping.phone)] || '').trim() : null;
            const company = mapping.company ? (row[parseInt(mapping.company)] || '').trim() : null;
            const notes = mapping.notes ? (row[parseInt(mapping.notes)] || '').trim() : null;
            const tagsRaw = mapping.tags ? (row[parseInt(mapping.tags)] || '').trim() : null;
            
            // Convert comma-separated tags string to array
            const tags: string[] = tagsRaw 
              ? tagsRaw.split(',').map(t => t.trim()).filter(Boolean)
              : [];

            // Intelligent name generation: if name is missing, use email or phone
            if (!name) {
              if (email) {
                // Extract name from email (part before @)
                name = email.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              } else if (phone) {
                name = `Contact ${phone}`;
              } else {
                // Skip rows with no name, email, or phone
                return null;
              }
            }

            // Validate email format
            if (email && !isValidEmail(email)) return null;

            // Check duplicates
            const cleanPhone = phone?.replace(/\D/g, '') || '';
            if (skipDuplicates) {
              if (email && (existingEmails.has(email) || seenEmails.has(email))) return null;
              if (cleanPhone && (existingPhones.has(cleanPhone) || seenPhones.has(cleanPhone))) return null;
            }

            // Track seen values
            if (email) seenEmails.add(email);
            if (cleanPhone) seenPhones.add(cleanPhone);

            // Normalize phone if enabled
            if (phone && normalizePhones) {
              phone = normalizePhone(phone);
            }

            return {
              name,
              email: email || null,
              phone: phone || null,
              company: company || null,
              notes_short: notes || null,
              source: 'manual' as const,
              temperature: 'warm' as const,
              pipeline_stage: 'new' as const,
              tags: tags, // Always array, never null
            };
          } catch (err) {
            console.warn('[CSVImport] Error processing row:', err);
            return null;
          }
        })
        .filter((lead): lead is NonNullable<typeof lead> => lead !== null);

      console.log('[CSVImport] Prepared', leads.length, 'leads for import');

      if (leads.length === 0) {
        setErrorMessage('No valid leads to import after validation.');
        setStep('error');
        return;
      }

      await bulkCreate.mutateAsync(leads);
      
      setImportSummary({
        ...calculatedSummary,
        valid: leads.length,
      });
      setStep('done');
      
      toast({
        title: 'Import Successful',
        description: `${leads.length} leads have been imported.`,
      });

    } catch (err) {
      // Full error logging with JSON.stringify
      console.error('[CSVImport] Import error:', JSON.stringify(err, null, 2));
      
      const errorMessage = err instanceof Error ? err.message : 'Import failed. Please try again.';
      setErrorMessage(errorMessage);
      setStep('error');
      
      // Show detailed error toast with message and details
      const errorDetails = err instanceof Error && (err as any).details 
        ? ` ${(err as any).details}` 
        : '';
      
      toast({
        title: 'Import Failed',
        description: `${errorMessage}${errorDetails}`,
        variant: 'destructive',
      });
    }
  };

  const handleClose = () => {
    setStep('select');
    setCsvData(null);
    setMapping({
      name: '',
      email: '',
      phone: '',
      company: '',
      source: '',
      notes: '',
      temperature: '',
    });
    setImportSummary(null);
    setSkipDuplicates(true);
    setNormalizePhones(true);
    setErrorMessage('');
    setIsLoading(false);
    onOpenChange(false);
  };

  const handleRetry = () => {
    setStep('select');
    setCsvData(null);
    setErrorMessage('');
    setIsLoading(false);
  };

  // Preview data for display
  const previewRows = csvData?.rows.slice(0, 5) || [];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-primary" />
            Import Leads from CSV
          </DialogTitle>
          <DialogDescription>
            {step === 'select' && 'Select a CSV file to import leads.'}
            {step === 'preview' && 'Review and map columns to lead fields.'}
            {step === 'confirm' && 'Confirm import settings and proceed.'}
            {step === 'importing' && 'Importing leads...'}
            {step === 'done' && 'Import complete!'}
            {step === 'error' && 'An error occurred.'}
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        {['select', 'preview', 'confirm'].includes(step) && (
          <div className="flex items-center justify-center gap-2 py-2">
            {['select', 'preview', 'confirm'].map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  step === s 
                    ? 'bg-primary text-primary-foreground' 
                    : ['select', 'preview', 'confirm'].indexOf(step) > i
                      ? 'bg-primary/20 text-primary'
                      : 'bg-muted text-muted-foreground'
                }`}>
                  {i + 1}
                </div>
                {i < 2 && (
                  <div className={`w-8 h-0.5 ${
                    ['select', 'preview', 'confirm'].indexOf(step) > i 
                      ? 'bg-primary' 
                      : 'bg-muted'
                  }`} />
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto py-4">
          {/* Step 1: Select File */}
          {step === 'select' && (
            <div className="py-4">
              <label className="flex flex-col items-center justify-center gap-4 border-2 border-dashed border-border rounded-lg p-8 cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                  {isLoading ? (
                    <Loader2 className="w-7 h-7 text-primary animate-spin" />
                  ) : (
                    <FileSpreadsheet className="w-7 h-7 text-primary" />
                  )}
                </div>
                <div className="text-center">
                  <p className="font-medium text-foreground">Choose CSV File</p>
                  <p className="text-sm text-muted-foreground mt-1">or drag and drop</p>
                </div>
                <input
                  type="file"
                  accept=".csv,text/csv,application/vnd.ms-excel"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={isLoading}
                />
              </label>
              <p className="text-xs text-muted-foreground text-center mt-4">
                Supported: .csv files • Maximum 10MB • Name column required
              </p>
            </div>
          )}

          {/* Step 2: Preview & Map */}
          {step === 'preview' && csvData && (
            <div className="space-y-4">
              {/* File Info */}
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <FileText className="w-5 h-5 text-primary" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{csvData.fileName}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(csvData.fileSize)} • {csvData.rows.length} rows • {csvData.headers.length} columns
                  </p>
                </div>
              </div>

              {/* Column Mapping */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium">Map Columns</h4>
                <div className="grid gap-2">
                  {LEAD_FIELDS.map(field => (
                    <div key={field.key} className="grid grid-cols-3 gap-3 items-center">
                      <Label className="text-sm">
                        {field.label}
                        {field.required && <span className="text-destructive ml-0.5">*</span>}
                      </Label>
                      <Select
                        value={mapping[field.key as keyof ColumnMapping] || '__skip__'}
                        onValueChange={(v) => setMapping({ ...mapping, [field.key]: v === '__skip__' ? '' : v })}
                      >
                        <SelectTrigger className="col-span-2">
                          <SelectValue placeholder="Skip this field" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__skip__">-- Skip --</SelectItem>
                          {csvData.headers.map((h, i) => (
                            <SelectItem key={i} value={String(i)}>
                              {h || `Column ${i + 1}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preview Table */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Preview (first 5 rows)</h4>
                <ScrollArea className="h-[140px] rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {csvData.headers.map((h, i) => (
                          <TableHead key={i} className="text-xs whitespace-nowrap">
                            {h || `Col ${i + 1}`}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewRows.map((row, i) => (
                        <TableRow key={i}>
                          {row.map((cell, j) => (
                            <TableCell key={j} className="text-xs py-1 whitespace-nowrap max-w-[150px] truncate">
                              {cell || '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </div>

              {/* Validation Alerts */}
              {!mapping.name && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertTitle>Required field missing</AlertTitle>
                  <AlertDescription>
                    Please map the Name field to continue.
                  </AlertDescription>
                </Alert>
              )}

              {mapping.name && !mapping.phone && !mapping.email && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    No contact field (Phone or Email) mapped. Consider mapping one for better lead management.
                  </AlertDescription>
                </Alert>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => setStep('confirm')} 
                  disabled={!mapping.name}
                  className="gap-2"
                >
                  Continue
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 3: Confirm & Import */}
          {step === 'confirm' && csvData && calculatedSummary && (
            <div className="space-y-4">
              {/* Import Options */}
              <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                <h4 className="text-sm font-medium">Import Options</h4>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="skipDuplicates" 
                      checked={skipDuplicates}
                      onCheckedChange={(checked) => setSkipDuplicates(checked === true)}
                    />
                    <label htmlFor="skipDuplicates" className="text-sm cursor-pointer">
                      Skip duplicates (by email or phone)
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="normalizePhones" 
                      checked={normalizePhones}
                      onCheckedChange={(checked) => setNormalizePhones(checked === true)}
                    />
                    <label htmlFor="normalizePhones" className="text-sm cursor-pointer">
                      Normalize phone numbers (remove spaces)
                    </label>
                  </div>
                </div>
              </div>

              {/* Import Summary */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Import Summary
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total rows:</span>
                    <span className="font-medium">{calculatedSummary.total}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Valid leads:</span>
                    <span className="font-medium text-success">{calculatedSummary.valid}</span>
                  </div>
                  {calculatedSummary.duplicates > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Duplicates (skipped):</span>
                      <span className="font-medium text-muted-foreground">{calculatedSummary.duplicates}</span>
                    </div>
                  )}
                  {calculatedSummary.invalid > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Invalid rows:</span>
                      <span className="font-medium text-destructive">{calculatedSummary.invalid}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Validation Errors */}
              {calculatedSummary.errors.length > 0 && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle>Validation Issues ({calculatedSummary.errors.length})</AlertTitle>
                  <AlertDescription>
                    <ScrollArea className="h-[80px] mt-2">
                      <ul className="text-xs space-y-1">
                        {calculatedSummary.errors.slice(0, 10).map((err, i) => (
                          <li key={i}>Row {err.row}: {err.reason}</li>
                        ))}
                        {calculatedSummary.errors.length > 10 && (
                          <li className="text-muted-foreground">
                            ... and {calculatedSummary.errors.length - 10} more
                          </li>
                        )}
                      </ul>
                    </ScrollArea>
                  </AlertDescription>
                </Alert>
              )}

              {calculatedSummary.valid === 0 && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertTitle>No valid leads</AlertTitle>
                  <AlertDescription>
                    Please go back and fix the column mapping or check your CSV data.
                  </AlertDescription>
                </Alert>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setStep('preview')} className="gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Button>
                <Button 
                  onClick={handleImport} 
                  disabled={calculatedSummary.valid === 0}
                  className="gap-2"
                >
                  Import {calculatedSummary.valid} Leads
                  <Upload className="w-4 h-4" />
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Importing State */}
          {step === 'importing' && (
            <div className="py-12 flex flex-col items-center gap-4">
              <Loader2 className="w-10 h-10 animate-spin text-primary" />
              <div className="text-center">
                <p className="font-medium">Importing leads...</p>
                <p className="text-sm text-muted-foreground">This may take a moment</p>
              </div>
            </div>
          )}

          {/* Done State */}
          {step === 'done' && importSummary && (
            <div className="py-8 flex flex-col items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-success" />
              </div>
              <div className="text-center">
                <p className="font-semibold text-lg">Import Complete!</p>
                <p className="text-muted-foreground mt-1">
                  {importSummary.valid} leads have been imported successfully.
                </p>
              </div>
              <Button onClick={handleClose} className="mt-4">
                Done
              </Button>
            </div>
          )}

          {/* Error State */}
          {step === 'error' && (
            <div className="py-8 flex flex-col items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center">
                <XCircle className="w-8 h-8 text-destructive" />
              </div>
              <div className="text-center max-w-sm">
                <p className="font-semibold text-lg">Import Failed</p>
                <p className="text-muted-foreground mt-1">{errorMessage || 'An unexpected error occurred.'}</p>
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button onClick={handleRetry}>
                  Try Again
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
